/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  ThemeProvider as StyledThemeProvider,
  useTheme
} from 'styled-components';
import { DirectionProvider } from '@veneer/core';
import { ThemeProvider as VThemeProvider } from '@veneer/theme';
import {
  UiTheme,
  MultiUiBrandProvider,
  ThemeProvider,
  getThemeProviderConfig,
  useMultiUiBrandContext
} from '@jarvis/react-setup-and-onboarding-addons';
import { useMfeOptions } from './MfeOptionsContext';
import { getShellDirection, getShellAppContext } from 'src/utils/shell';
import { InitializationMethod } from 'src/types/mfeProps';
import { useColorMode } from 'src/hooks/useColorMode';

export interface AppTheme {
  isHpx: boolean;
  backgroundColor: string;
  foregroundColor: string;
  isModal?: boolean;
}

type AppThemeProviderProps = {
  children: React.ReactNode;
};

export const AppThemeProvider = ({
  children
}: AppThemeProviderProps): React.ReactElement => {
  const { uiBrand } = useMultiUiBrandContext();
  const {
    options: { initializationMethod }
  } = useMfeOptions();
  const appContext = getShellAppContext();
  const direction = getShellDirection(React);
  const uiMode = useColorMode();

  // This logic will cause the HPX UI to also be used during HPSmart.
  // TODO: Get a way to differentiate between HPX and HPSmart so that we can apply the HPX theme only to HPX.
  const finalUiBrand = appContext !== 'WEB_PORTAL' ? UiTheme.hpx : uiBrand;
  const isHpx = finalUiBrand === UiTheme.hpx;

  const colorMap: Record<string, { background: string; foreground: string }> = {
    light: { background: 'rgb(255, 255, 255)', foreground: 'rgb(41, 42, 53)' },
    dark: { background: 'rgb(41, 42, 53)', foreground: 'rgb(255, 255, 255)' },
    highContrast: { background: 'Window', foreground: 'WindowText' }
  };

  const { background: backgroundColor, foreground: foregroundColor } =
    colorMap[uiMode] ?? colorMap.light;

  const theme: AppTheme = {
    isHpx,
    backgroundColor,
    foregroundColor,
    isModal: initializationMethod === InitializationMethod.Modal
  };

  return (
    <DirectionProvider direction={direction}>
      <MultiUiBrandProvider uiBrand={finalUiBrand}>
        <VThemeProvider {...getThemeProviderConfig(finalUiBrand, uiMode)}>
          <ThemeProvider mode={uiMode}>
            <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
          </ThemeProvider>
        </VThemeProvider>
      </MultiUiBrandProvider>
    </DirectionProvider>
  );
};

export { useTheme as useAppTheme };
